/**
 * AutoHeightIframe Component
 * A reusable iframe that dynamically adjusts its height to match its content.
 * 
 * Supports two modes:
 * - src (URL): Fetches the HTML to bypass CORS for height calculation, falls back to standard src on error.
 * - content (HTML string via srcDoc): for rendering raw HTML fragments directly.
 */
import * as React from 'react'
import styled from 'styled-components'

interface Props {
  src?: string
  content?: string
  title?: string
  minHeight?: string
  allow?: string
}

interface State {
  iframeHeight: string
  fetchedContent: string | null
  fetchError: boolean
}

const StyledIframe = styled.iframe`
  width: 100%;
  border: none;
  display: block;
  transition: height 0.2s ease-in-out;
  background: transparent;
`

class AutoHeightIframe extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      iframeHeight: props.minHeight || 'auto',
      fetchedContent: null,
      fetchError: false
    }
  }
  
  private iframeRef = React.createRef<HTMLIFrameElement>()
  private resizeObserver: any = null
  private abortController: AbortController | null = null

  componentDidMount() {
    this.loadRemoteContent()
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.src !== this.props.src) {
      this.loadRemoteContent()
    }
  }

  componentWillUnmount() {
    if (this.abortController) {
      this.abortController.abort()
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  // Pre-process HTML content to convert vh units to static px values
  getProcessedContent = (rawContent: string) => {
    if (!rawContent || typeof window === 'undefined') {
      return rawContent
    }

    const windowHeight = window.innerHeight
    return rawContent.replace(/(\d+(?:\.\d+)?)vh/gi, (_match: string, val: string) => {
      const pixels = (parseFloat(val) * windowHeight) / 100
      return `${pixels}px`
    })
  }

  loadRemoteContent = async () => {
    const { src, content } = this.props
    
    // Only execute the fetch if a URL is provided and raw content isn't already taking precedence
    if (content || !src) {
      return
    }

    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    try {
      const response = await fetch(src, { signal: this.abortController.signal })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const htmlText = await response.text()
      const processedHtml = this.getProcessedContent(htmlText)
      this.setState({ fetchedContent: processedHtml, fetchError: false })
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return
      }
      console.warn('Failed to fetch iframe content for srcDoc, falling back to standard src attribute')
      this.setState({ fetchError: true, fetchedContent: null })
    }
  }

  calculateHeight = () => {
    const iframe = this.iframeRef.current
    if (!iframe || !iframe.contentWindow) {
      return
    }

    try {
      const iframeDoc = iframe.contentWindow.document
      const body = iframeDoc.body
      const html = iframeDoc.documentElement

      if (body && html) {
        const height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        )

        if (height > 0) {
          // Small buffer to prevent scrollbar flickering
          const nextHeight = `${height + 20}px`
          if (nextHeight !== this.state.iframeHeight) {
            this.setState({ iframeHeight: nextHeight })
          }
        }
      }
    } catch (_e) {
      // Cross-origin iframe — cannot access contentWindow, keep fallback height
    }
  }

  handleIframeLoad = () => {
    this.calculateHeight()
    // Set up ResizeObserver for dynamic content
    const iframe = this.iframeRef.current
    if (!iframe || !iframe.contentWindow) {
      return
    }

    try {
      const body = iframe.contentWindow.document.body
      const win = window as any

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }

      if (win.ResizeObserver && body) {
        this.resizeObserver = new win.ResizeObserver(() => {
          this.calculateHeight()
        })
        this.resizeObserver.observe(body)
      }
    } catch (_e) {
      // Cross-origin — no observer needed
    }
  }

  render() {
    const { content, title = '', allow } = this.props
    const { iframeHeight, fetchedContent, fetchError } = this.state

    // Determine the source of truth for the HTML
    const activeContent = content ? this.getProcessedContent(content) : fetchedContent
    const useSrcDoc = Boolean(activeContent && !fetchError)

    const iframeProps: Record<string, any> = {
      innerRef: this.iframeRef,
      title,
      onLoad: this.handleIframeLoad,
      style: { height: iframeHeight }
    }

    // Conditionally assign srcDoc or src based on fetch success
    if (useSrcDoc) {
      iframeProps.srcDoc = activeContent
    } else {
      iframeProps.style = { display: 'none' }
    }

    if (allow) {
      iframeProps.allow = allow
    }

    return <StyledIframe {...iframeProps} />
  }
}

export default AutoHeightIframe


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/components/AutoHeightIframe/index.tsx